import { Link } from 'react-router-dom';
import { LegalDoc, LegalSection, LegalList } from '../components/LegalDoc.js';

const CONTACT = 'hello@gilding.app';

/**
 * Terms of Use: the rules for using Gilding: acceptable use, credits/payments,
 * ownership of what you make, and the usual disclaimers. Public, plain-language.
 */
export function Terms() {
  return (
    <LegalDoc
      title="Terms of Use"
      updated="September 8, 2026"
      intro={
        <>
          By using Gilding you agree to these terms. They&rsquo;re written to be readable; where they
          use &ldquo;we&rdquo; or &ldquo;Gilding&rdquo; that means the service and its operator.
        </>
      }
    >
      <LegalSection title="Using Gilding">
        <p>
          Gilding turns your sketches into images using AI models. You must be at least 18 and able
          to form a binding agreement. Some features require signing in with a crypto wallet; certain
          features are currently limited to Whacky Whales NFT holders during
          early access.
        </p>
      </LegalSection>

      <LegalSection title="Acceptable use">
        <p>Prompts are screened for safety before any image is made. You agree not to use Gilding to create or upload:</p>
        <LegalList
          items={[
            'Content that infringes someone else’s copyright, trademark, or other rights.',
            'Sexual content involving minors, or non-consensual or sexually explicit imagery.',
            'Realistic depictions of real, identifiable people without their consent, including public figures.',
            'Harassing, hateful, deceptive, or unlawful content.',
          ]}
        />
        <p>
          We may refuse, remove, or limit content and access that breaks these rules or abuses the
          service.
        </p>
      </LegalSection>

      <LegalSection title="Your content & ownership">
        <LegalList
          items={[
            <>
              <strong className="text-ink">What you upload.</strong> You keep your rights to images
              you upload as references, and you confirm you have the right to use them. Don&rsquo;t
              upload anything you don&rsquo;t have permission to use.
            </>,
            <>
              <strong className="text-ink">What you generate.</strong> As between you and us, the
              gildings you create are yours to use, subject to these terms and the underlying image
              providers&rsquo; terms. AI-generated output may not be eligible for copyright in every
              jurisdiction.
            </>,
            <>
              <strong className="text-ink">Publishing.</strong> If you publish a gilding to the public
              feed or a share page, you grant us a license to host, display, and promote it within
              Gilding. You can unpublish or delete it.
            </>,
          ]}
        />
      </LegalSection>

      <LegalSection title="Credits & payments">
        <LegalList
          items={[
            'Gildings are paid for with credits ("leaves"). New accounts and free gildings are provided at our discretion and may be limited or changed.',
            'Credit packs are bought by sending USDC on Base from your wallet to the receiving address shown at checkout. Leaves are credited after payment verification. On-chain payments are final and irreversible.',
            'Credits are a prepaid access mechanism for the service. They have no cash value, are non-transferable, are non-refundable, and may expire or be adjusted if the service changes.',
          ]}
        />
      </LegalSection>

      <LegalSection title="NFTs & early access">
        <p>
          Gilding reads public NFT ownership on Ethereum to determine early access. These checks do not take custody of or move
          your NFTs or tokens. Signing in does not authorize a payment; purchases require separate
          wallet approval. Holder access is a service benefit and promises no
          investment return.
        </p>
        <p>
          Gilding is an independent project and is not affiliated with, sponsored by, or endorsed by
          the Whacky Whales collection or its creators. Access tied to holding an NFT is a product
          feature, not financial or investment advice or a recommendation to buy. NFTs carry risk, so
          do your own research. Disclosure: the operator of Gilding holds Whacky Whales NFTs.
        </p>
      </LegalSection>


      <LegalSection title="Service &ldquo;as is&rdquo;">
        <p>
          Gilding is provided &ldquo;as is,&rdquo; without warranties of any kind. AI output varies
          and may be imperfect, unexpected, or unavailable at times. We don&rsquo;t guarantee any
          particular result, uptime, or that generated content is unique or free of resemblance to
          existing work.
        </p>
      </LegalSection>

      <LegalSection title="Limitation of liability">
        <p>
          To the fullest extent permitted by law, Gilding and its operator are not liable for
          indirect, incidental, or consequential damages arising from your use of the service. Where
          liability can&rsquo;t be excluded, it is limited to the amount you paid us in the prior
          three months.
        </p>
      </LegalSection>

      <LegalSection title="Termination">
        <p>
          You can stop using Gilding at any time. We may suspend or end access that violates these
          terms or that we must restrict for legal or operational reasons.
        </p>
      </LegalSection>

      <LegalSection title="Changes & contact">
        <p>
          We may update these terms as the product evolves; the date above marks the latest version,
          and continued use means you accept the changes. Questions? Reach us at{' '}
          <a href={`mailto:${CONTACT}`} className="text-accent underline-offset-2 hover:underline">
            {CONTACT}
          </a>
          . See also our{' '}
          <Link to="/privacy" className="text-accent underline-offset-2 hover:underline">
            Privacy policy
          </Link>
          .
        </p>
      </LegalSection>
    </LegalDoc>
  );
}
